// src/features/streams/components/StreamPreview.tsx

import { AspectRatio, Center, Stack, Text, Loader } from '@mantine/core';
import { IconBroadcastOff } from '@tabler/icons-react';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { Hostname } from '@lib2/model/common/ServiceHosts';
import { usePlayers } from '@/features/players/usePlayers';
import type { StreamViewData } from '@/lib/types/Stream';
import type IMediaView from '@lib2/model/media/IMediaView';

interface StreamPreviewProps {
  stream: StreamViewData;
  isLive: boolean;
}

export function StreamPreview({ stream, isLive }: StreamPreviewProps) {
  const { defaultPlayerId } = usePlayers();

  // Live: show player iframe
  if (isLive) {
    if (!defaultPlayerId) {
      return (
        <AspectRatio ratio={16 / 9}>
          <Center bg="gray.1" style={{ borderRadius: 8 }}>
            <Stack align="center" gap="sm">
              <Loader size="lg" />
              <Text c="dimmed">Loading player...</Text>
            </Stack>
          </Center>
        </AspectRatio>
      );
    }

    const mediaView = { data: stream } as unknown as IMediaView;
    const isReseller = Hostname.isResellerHostname();
    const linker = new MediaPlayerLinker(defaultPlayerId, mediaView, isReseller);
    const previewUrl = linker.getPreviewUrl(false, { isPreview: true });

    return (
      <AspectRatio ratio={16 / 9}>
        <iframe
          src={previewUrl}
          style={{ border: 'none', width: '100%', height: '100%' }}
          allowFullScreen
          title={stream.media.title}
        />
      </AspectRatio>
    );
  }

  // Offline: show placeholder
  return (
    <AspectRatio ratio={16 / 9}>
      <Center bg="gray.1" style={{ borderRadius: 8 }}>
        <Stack align="center" gap="sm">
          <IconBroadcastOff size={48} color="var(--mantine-color-gray-5)" />
          <Text c="dimmed">Stream is offline</Text>
        </Stack>
      </Center>
    </AspectRatio>
  );
}
